import React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { setSidedrawerIsOpen } from '@om/redux/actions/publicWeb/naPersonalLoansCallMeBack';

import OmButton from '../forms/OmButton';
import OmInputFieldWrapper from '../forms/OmInputFieldWrapper';
import OmDropdownField from '../forms/OmDropdownField';

import { OmPersonalLoansCalculator, OmCalculatorResult } from '@om/component-library-react';
import {
    getAmountBorrowed,
    getCalculationResults,
    getCustomAmountBorrowed,
    getCustomRepaymentDuration,
    getRepaymentDuration,
} from '@om/redux/selectors/namPersonalLoansCalculator/calculator';
import {
    setAmountBorrowedValue,
    setCustomAmountBorrowedValue,
    setRepaymentDurationValue,
    setCustomRepaymentDurationValue,
    requestLoanResults,
    resetForm,
} from '@om/redux/actions/namPersonalLoansCalculator/calculator';
import { applyThousandSeperator, createActionHandler } from '../../util';

const PersonalLoansCalculator = ({ uid, isInContainer, onCompleteEvent, slot }) => {
    const dispatch = useDispatch();
    const [showLoanRepaymentDurationForm, setShowLoanRepaymentDurationForm] = useState(false);

    const { amountBorrowed, customAmountBorrowed, repaymentDuration, customRepaymentDuration, calculationResults } =
        useSelector(
            createStructuredSelector({
                amountBorrowed: getAmountBorrowed,
                customAmountBorrowed: getCustomAmountBorrowed,
                repaymentDuration: getRepaymentDuration,
                customRepaymentDuration: getCustomRepaymentDuration,
                calculationResults: getCalculationResults,
            })
        );

    const navigationButtonHandler = () => {
        setShowLoanRepaymentDurationForm(!showLoanRepaymentDurationForm);
    };

    const onCalculateEventHandler = () => {
        dispatch(requestLoanResults());
        onCompleteEvent && onCompleteEvent();
    };

    const updateFormField = (value, setter) => dispatch(setter(value));

    return (
        <OmPersonalLoansCalculator id={uid} slot={slot} isInContainer={isInContainer}>
            <div slot="calculator-form">
                <form>
                    {!showLoanRepaymentDurationForm && (
                        <div>
                            <OmDropdownField
                                useNativeMobileDropdown={true}
                                placeholder="How much do you need?"
                                required={true}
                                id="loanAmount"
                                formId="loanAmountDetailsForm"
                                name="loanAmount"
                                align="left"
                                filterable={false}
                                onSelectHandler={(event) => {
                                    updateFormField(event.detail.id, setAmountBorrowedValue);
                                }}
                                reduxValue={amountBorrowed}
                                options={[
                                    { value: '100000', label: 'N$100 000' },
                                    { value: '50000', label: 'N$50 000' },
                                    { value: '20000', label: 'N$20 000' },
                                    { value: '10000', label: 'N$10 000' },
                                    { value: '5000', label: 'N$5 000' },
                                    { value: 'custom', label: 'Own amount' },
                                ]}
                            />
                            {amountBorrowed.value === 'custom' && (
                                <OmInputFieldWrapper
                                    persistHandler={createActionHandler(dispatch, setCustomAmountBorrowedValue)}
                                    reduxValue={customAmountBorrowed}
                                    placeholder="Enter your own loan amount"
                                    required="true"
                                    align="left"
                                    prefixString="N$"
                                    mask="thousands-spaced"
                                    formId="otherLoanAmount"
                                    keyboardMode="numeric"
                                    hintText="Please enter a loan amount of N$3 000 or more"
                                />
                            )}
                        </div>
                    )}
                    {showLoanRepaymentDurationForm && (
                        <div>
                            <OmDropdownField
                                useNativeMobileDropdown={true}
                                placeholder="How long do you need to repay it?"
                                required={true}
                                id="repaymentDuration"
                                formId="repaymentDurationDetailsForm"
                                name="repaymentDuration"
                                align="left"
                                filterable={false}
                                onSelectHandler={(event) => {
                                    updateFormField(event.detail.id, setRepaymentDurationValue);
                                }}
                                reduxValue={repaymentDuration}
                                options={[
                                    { value: 6, label: '6 Months' },
                                    { value: 12, label: '12 Months' },
                                    { value: 24, label: '24 Months' },
                                    { value: 36, label: '36 Months' },
                                    { value: 48, label: '48 Months' },
                                    { value: 60, label: '60 Months' },
                                    {
                                        value: 'custom',
                                        label: 'Own Number of Months',
                                    },
                                ]}
                            />
                            {repaymentDuration.value === 'custom' && (
                                <OmInputFieldWrapper
                                    persistHandler={createActionHandler(dispatch, setCustomRepaymentDurationValue)}
                                    reduxValue={customRepaymentDuration}
                                    placeholder="Enter your own repayment term"
                                    required="true"
                                    align="left"
                                    mask="thousands-spaced"
                                    formId="otherLoanAmount"
                                    keyboardMode="numeric"
                                    hintText={'Please enter a repayment term from 6 to 60 months'}
                                    hardcode
                                    this
                                />
                            )}
                        </div>
                    )}
                </form>
            </div>
            <div slot="navigation-buttons-web">
                <OmButton
                    text={showLoanRepaymentDurationForm ? 'back' : 'next'}
                    type="secondary"
                    size="large"
                    disabled={
                        amountBorrowed.value === 'custom'
                            ? !customAmountBorrowed.value || customAmountBorrowed.status !== 'valid'
                            : !amountBorrowed.value
                    }
                    onClick={navigationButtonHandler}
                />
                {showLoanRepaymentDurationForm && (
                    <div className="continue-section">
                        <OmButton
                            text="Calculate"
                            type="primary"
                            size="large"
                            disabled={
                                repaymentDuration.value === 'custom'
                                    ? !customRepaymentDuration.value || customRepaymentDuration.status !== 'valid'
                                    : !repaymentDuration.value
                            }
                            onClick={() => onCalculateEventHandler()}
                        />
                        {true && (
                            <h6 className="small" slot="navigation-button-disclaimer">
                                <span className="medium">Note:</span>
                                The repayment amount is only an estimate and includes credit life insurance and service
                                fees{' '}
                            </h6>
                        )}
                    </div>
                )}
            </div>
            <div slot="result-card">
                {calculationResults.status === 'success' && (
                    <OmCalculatorResult>
                        <div slot="row-1-col-1">
                            <h5 className="no-bottom-margin no-top-margin">
                                <strong>Your estimated total loan repayment</strong>
                            </h5>
                            <p className="no-bottom-margin no-top-margin">
                                N${applyThousandSeperator(calculationResults.results.totalLoanCost)}
                            </p>
                        </div>

                        <div slot="row-2-col-1">
                            <p className="no-bottom-margin no-top-margin">Your estimated monthly repayment will be</p>
                            <h5 className="no-bottom-margin no-top-margin">
                                <strong>
                                    N${applyThousandSeperator(calculationResults.results.totalMonthlyInstallment)}
                                </strong>
                            </h5>
                        </div>

                        <div slot="row-2-col-2">
                            <OmButton
                                slot="recalculate-mobile"
                                type="secondary"
                                size="small"
                                text="Recalculate"
                                withIcon={false}
                                onClick={(event) => {
                                    setShowLoanRepaymentDurationForm(false);
                                    dispatch(resetForm());
                                }}
                            />
                        </div>

                        <h6 slot="end-heading">
                            <strong>Ready to apply for your loan?</strong>
                        </h6>

                        <p slot="end-text">
                            Get a personal loan of up to <br /> N$100 000 and flexible monthly payments. It's quick and
                            easy.
                        </p>

                        <OmButton
                            slot="end-button"
                            type="primary"
                            size="small"
                            text="Apply now"
                            onClick={() => dispatch(setSidedrawerIsOpen(true))}
                        />
                    </OmCalculatorResult>
                )}
            </div>
        </OmPersonalLoansCalculator>
    );
};

export default PersonalLoansCalculator;
